import type { Solution } from '../types/platform'

export const SOLUTIONS: Solution[] = [
  {
    id: 'invest',
    label: 'Invest',
    descriptor: 'Investment due diligence',
    headline: 'Investment Due Diligence',
    line: 'Source, qualify, diligence, decide and monitor investments.',
    tagsLabel: 'For',
    tags: ['VC', 'CVC', 'Private equity', 'Family offices', 'Angel networks', 'LPs'],
    flow: {
      input: 'Pitch decks, inbound email, applications, data rooms',
      agents: ['Thesis Fit', 'Research', 'Financial Diligence', 'Investment Memo'],
      workflow: 'Screen → Meet → Diligence → IC',
      decision: 'Investment committee decides',
      monitoring: 'Portfolio Monitoring, Reporting',
    },
    page: {
      title: 'Diligence every investment against your thesis.',
      example:
        'An investment team screens a company against its thesis, reviews the evidence, follows up on open questions, and prepares an investment memo.',
      steps: [
        { label: 'Screen', detail: 'Assess the company against your thesis.', items: ['Thesis Fit Agent', 'Industry Classification Agent'] },
        { label: 'Review evidence', detail: 'Check claims against the deck and data room.', items: ['Research Agent', 'Financial Due Diligence'] },
        { label: 'Follow up', detail: 'Request answers to open questions.', items: ['Information Request Agent', 'Founder Communication Agent'] },
        { label: 'Prepare memo', detail: 'Draft the memo in your template.', items: ['Investment Memo Agent', 'IC Template Agent'] },
        { label: 'Decide', detail: 'Your investment committee reviews and decides.', items: ['Your team'] },
      ],
      decisionOwner: 'Investment committee',
      decisionOutput: {
        title: 'An investment memo ready for IC review',
        items: [
          'Thesis fit by criterion, with the evidence behind each call',
          'Sourced strengths and risks',
          'Open questions and the status of each follow-up',
          'A drafted recommendation for partners to challenge — your IC decides',
        ],
      },
      visual: 'memo',
      proofOrgs: ['Blue Vision Capital', 'Rogers Accelerator'],
      seo: {
        title: 'Investment Due Diligence Software | DiligenceGPT™',
        description:
          'AI agents for investment due diligence: screen companies against your thesis, review evidence, follow up on open questions and prepare investment memos for IC. For VC, CVC, PE, family offices and LPs.',
        keywords: ['investment due diligence software', 'AI investment memo', 'investment thesis screening', 'VC due diligence'],
      },
    },
  },
  {
    id: 'buy',
    label: 'Buy',
    descriptor: 'Procurement and vendor due diligence',
    headline: 'Procurement & Vendor Due Diligence',
    line: 'Evaluate vendors, suppliers and pilots before they become dependencies.',
    tagsLabel: 'Use cases',
    tags: ['Technology procurement', 'Vendor onboarding', 'Startup pilots', 'Third-party risk'],
    flow: {
      input: 'Vendor submissions, proposals, security questionnaires',
      agents: ['Requirements Fit', 'Cybersecurity Diligence', 'Vendor Diligence', 'Procurement Assessment'],
      workflow: 'Requirements → Security → Financial → Approval',
      decision: 'Procurement approves',
      monitoring: 'Vendor Monitoring, Contract Milestones',
    },
    page: {
      title: 'Choose vendors on evidence, not on the pitch.',
      example:
        'A buyer compares two vendors against the same requirements, requests a missing security document, and prepares an approval recommendation.',
      steps: [
        { label: 'Receive', detail: 'Collect vendor submissions and questionnaires.', items: ['Web Application Agent', 'File Upload Agent'] },
        { label: 'Compare', detail: 'Check each vendor against the same requirements.', items: ['Requirements Fit Agent', 'Vendor Due Diligence'] },
        { label: 'Request', detail: 'Ask vendors for missing documents.', items: ['Vendor Communication Agent', 'Information Request Agent'] },
        { label: 'Review risk', detail: 'Assess security and financial exposure.', items: ['Cybersecurity Due Diligence', 'Financial Due Diligence'] },
        { label: 'Recommend', detail: 'Draft the approval recommendation.', items: ['Procurement Assessment Agent', 'Approval Agent'] },
      ],
      decisionOwner: 'Procurement approver',
      decisionOutput: {
        title: 'An approval recommendation',
        items: [
          'Side-by-side coverage of every requirement',
          'Evidence status for each requirement',
          'Outstanding vendor documents and who owns the request',
          'Reviewer notes and a drafted recommendation for the approver',
        ],
      },
      visual: 'vendor',
      seo: {
        title: 'Vendor & Procurement Due Diligence Software | DiligenceGPT™',
        description:
          'Evaluate vendors, suppliers and startup pilots against the same requirements, request missing security documents and prepare approval recommendations with AI agents.',
        keywords: ['vendor due diligence software', 'procurement due diligence', 'third-party risk assessment', 'vendor onboarding'],
      },
    },
  },
  {
    id: 'acquire',
    label: 'Acquire',
    descriptor: 'Acquisition due diligence',
    headline: 'Acquisition Due Diligence',
    line: 'Coordinate every workstream around an acquisition target.',
    tagsLabel: 'Workstreams',
    tags: ['Commercial', 'Financial', 'Technical', 'Organizational', 'Strategic'],
    flow: {
      input: 'Target data room, management materials, market data',
      agents: ['Data Room', 'Commercial Diligence', 'Technology Diligence', 'Risk Report'],
      workflow: 'Screen → Workstreams → Committee',
      decision: 'Deal team and committee decide',
      monitoring: 'Integration milestones, Risk Change',
    },
    page: {
      title: 'One brief across every acquisition workstream.',
      example:
        'A deal lead brings five reviewers’ findings into an acquisition brief and identifies the issues that need resolution before signing.',
      steps: [
        { label: 'Open data room', detail: 'Index the target’s materials.', items: ['Data Room Agent'] },
        { label: 'Assign', detail: 'Route each workstream to its owner.', items: ['Workflow Builder', 'Routing Agent'] },
        { label: 'Investigate', detail: 'Run each workstream’s analysis.', items: ['Commercial Due Diligence', 'Technology Due Diligence', 'Financial Due Diligence'] },
        { label: 'Consolidate', detail: 'Combine findings and flag conflicts.', items: ['Contradiction Agent', 'Risk Report Agent'] },
        { label: 'Pre-signing review', detail: 'Committee reviews open issues.', items: ['Committee Agent'] },
      ],
      decisionOwner: 'Deal team and committee',
      decisionOutput: {
        title: 'An acquisition brief',
        items: [
          'Findings from every workstream, with an owner for each',
          'Conflicts between workstreams flagged',
          'Issues that need resolution before signing',
          'A brief your deal team and committee can challenge',
        ],
      },
      visual: 'workstreams',
      seo: {
        title: 'Acquisition Due Diligence Software | DiligenceGPT™',
        description:
          'Coordinate commercial, financial, technical, organizational and strategic workstreams around an acquisition target and consolidate findings into one brief before signing.',
        keywords: ['acquisition due diligence', 'M&A due diligence software', 'data room analysis', 'deal workstream tracker'],
      },
    },
  },
  {
    id: 'select',
    label: 'Select',
    descriptor: 'Applications, grants and RFPs',
    headline: 'Applications, Grants & RFPs',
    line: 'Receive, enrich, score and evaluate high volumes consistently.',
    tagsLabel: 'For',
    tags: ['Accelerators', 'Government programs', 'Universities', 'Corporate programs', 'Competitions'],
    flow: {
      input: 'Web applications, proposals, supporting files',
      agents: ['Eligibility', 'Scoring', 'Applicant Communication', 'Grant Assessment'],
      workflow: 'Eligibility → Scoring → Panel review',
      decision: 'Selection panel decides',
      monitoring: 'Grant Milestones, Reporting',
    },
    page: {
      title: 'Score every application against the same rubric.',
      example:
        'A program team checks eligibility, scores applications against a shared rubric, and records why each shortlisted applicant advanced.',
      steps: [
        { label: 'Receive', detail: 'Collect applications from every channel.', items: ['Web Application Agent', 'Email Intake Agent'] },
        { label: 'Check eligibility', detail: 'Apply the program’s rules.', items: ['Eligibility Agent'] },
        { label: 'Enrich', detail: 'Fill gaps in each application.', items: ['Company Enrichment Agent', 'Gap Analysis Agent'] },
        { label: 'Score', detail: 'Apply the shared rubric.', items: ['Scoring Agent', 'Requirements Fit Agent'] },
        { label: 'Shortlist', detail: 'Panel reviews and records reasons.', items: ['Review Agent', 'Grant Assessment Agent'] },
      ],
      decisionOwner: 'Selection panel',
      decisionOutput: {
        title: 'A shortlist with recorded reasons',
        items: [
          'An eligibility result for every applicant',
          'Rubric scores with the evidence behind each',
          'Reviewer comments in one place',
          'A recorded reason for every shortlisted applicant',
        ],
      },
      visual: 'scorecard',
      proofOrgs: ['Build In Tulsa'],
      seo: {
        title: 'Application, Grant & RFP Review Software | DiligenceGPT™',
        description:
          'Receive, enrich and score high volumes of applications, grant proposals and RFP responses against a shared rubric, and record why each shortlisted applicant advanced.',
        keywords: ['application review software', 'grant application scoring', 'RFP evaluation', 'accelerator application review'],
      },
    },
  },
  {
    id: 'assure',
    label: 'Assure',
    descriptor: 'Organizational and risk due diligence',
    headline: 'Organizational & Risk Due Diligence',
    line: 'Assess governance, financial health, compliance, cybersecurity and operational risk.',
    tagsLabel: 'Covers',
    tags: ['Counterparties', 'Partners', 'Suppliers', 'Grantees'],
    flow: {
      input: 'Policies, questionnaires, financial reports',
      agents: ['Risk Screening', 'Organizational Diligence', 'Verification', 'Risk Report'],
      workflow: 'Screen → Assess → Review → Approve',
      decision: 'Risk owner decides',
      monitoring: 'Risk Change, Financial Monitoring',
    },
    page: {
      title: 'Separate verified evidence from outstanding requests.',
      example:
        'A reviewer assesses a prospective partner and separates verified evidence from outstanding requests before recommending approval.',
      steps: [
        { label: 'Collect', detail: 'Gather policies, reports and questionnaires.', items: ['File Upload Agent', 'Data Room Agent'] },
        { label: 'Screen', detail: 'Identify the risk areas that matter.', items: ['Risk Screening Agent'] },
        { label: 'Verify', detail: 'Check claims against evidence.', items: ['Verification Agent', 'Organizational Due Diligence'] },
        { label: 'Request', detail: 'Ask for what’s missing.', items: ['Information Request Agent'] },
        { label: 'Recommend', detail: 'Draft the approval recommendation.', items: ['Risk Report Agent', 'Approval Agent'] },
      ],
      decisionOwner: 'Risk owner',
      decisionOutput: {
        title: 'A risk register and approval recommendation',
        items: [
          'Findings organized by risk area',
          'Verified evidence kept apart from outstanding requests',
          'An owner and next action for every item',
          'A recommendation your risk owner approves or rejects',
        ],
      },
      visual: 'risk',
      seo: {
        title: 'Organizational & Risk Due Diligence Software | DiligenceGPT™',
        description:
          'Assess counterparties, partners, suppliers and grantees across governance, financial health, compliance, cybersecurity and operational risk, with verified evidence kept apart from open requests.',
        keywords: ['organizational due diligence', 'counterparty risk assessment', 'supplier risk due diligence', 'risk register'],
      },
    },
  },
  {
    id: 'monitor',
    label: 'Monitor',
    descriptor: 'Continuous due diligence',
    headline: 'Continuous Due Diligence',
    line: 'Keep watching after the decision is made.',
    tagsLabel: 'Monitors',
    tags: ['Portfolio companies', 'Vendors', 'Grant recipients', 'Counterparties'],
    flow: {
      input: 'Updates, reports, new public information',
      agents: ['Company Update', 'Financial Monitoring', 'Risk Change', 'Reporting'],
      workflow: 'Collect → Compare → Escalate',
      decision: 'Owner decides whether to reassess',
      monitoring: 'Continuous, on your schedule',
    },
    page: {
      title: 'Know when something changes after the decision.',
      example: 'A reviewer compares a new update with an agreed milestone, flags a delay, and assigns a follow-up.',
      steps: [
        { label: 'Collect updates', detail: 'Receive reports and new information.', items: ['Company Update Agent', 'Email Intake Agent'] },
        { label: 'Compare', detail: 'Check updates against commitments.', items: ['Grant Milestone Agent', 'Contract Milestone Agent'] },
        { label: 'Flag', detail: 'Surface delays and new risk.', items: ['Risk Change Agent', 'Financial Monitoring Agent'] },
        { label: 'Follow up', detail: 'Assign an owner and a next step.', items: ['Follow Up Agent', 'Escalation Agent'] },
        { label: 'Report', detail: 'Keep stakeholders current.', items: ['Reporting Agent'] },
      ],
      decisionOwner: 'Relationship owner',
      decisionOutput: {
        title: 'Milestone status and an assigned follow-up',
        items: [
          'The latest update compared with the original commitment',
          'Flags for delays or new risk',
          'A follow-up assigned to an owner',
          'The next review date',
        ],
      },
      visual: 'timeline',
      seo: {
        title: 'Continuous Due Diligence & Monitoring Software | DiligenceGPT™',
        description:
          'Monitor portfolio companies, vendors, grant recipients and counterparties after the decision: compare updates with commitments, flag delays and assign follow-ups.',
        keywords: ['continuous due diligence', 'portfolio monitoring', 'vendor monitoring', 'grant milestone tracking'],
      },
    },
  },
]

export function getSolution(id: string) {
  return SOLUTIONS.find((solution) => solution.id === id)
}

export const solutionPath = (id: string) => `/solutions/${id}`
